import clsx from 'clsx';
import { useState, useEffect, type ReactNode } from 'react';

import { Tabs } from '@/components/Tabs';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getSettings } from '@/service/settingsServices';
import { ISettingsData } from '@/types';

import AccountSecurity from './tabs/AccountSecurity';
import DeletedRecords from './tabs/DeletedRecords';
import General from './tabs/General';
import Language from './tabs/Language';
import Profile from './tabs/Profile';
import Roles from './tabs/Roles';

type TTabValue =
  | 'profile'
  | 'general'
  | 'roles'
  | 'language'
  | 'account_security'
  | 'deleted_records';

const Settings = () => {
  const { formatText } = useFormatText();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { hasAccess } = useAccess();
  const canAccessSettings = hasAccess(ERoleAccess.SETTINGS_ROLES);
  const canAccessUserManagement = hasAccess(ERoleAccess.USER_MANAGEMENT);

  const [activeTab, setActiveTab] = useState<TTabValue>('profile');
  const [rolesAction, setRolesAction] = useState<ReactNode>(null);
  const [settingsData, setSettingsData] = useState<ISettingsData | null>(null);

  const pageTitle = formatText('settings.title');

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: pageTitle, to: '/settings' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  useEffect(() => {
    handleGetSettings();
  }, []);

  const tabs = [
    {
      label: formatText('settings.tabs.profile'),
      value: 'profile' as const,
    },
    {
      label: formatText('settings.tabs.general-settings'),
      value: 'general' as const,
    },
    ...(canAccessSettings
      ? [
          {
            label: formatText('settings.tabs.roles-permissions'),
            value: 'roles' as const,
          },
        ]
      : []),
    {
      label: formatText('settings.tabs.language-region'),
      value: 'language' as const,
    },
    {
      label: formatText('settings.tabs.account-security'),
      value: 'account_security' as const,
    },
    ...(canAccessUserManagement
      ? [
          {
            label: formatText('settings.tabs.deleted-records'),
            value: 'deleted_records' as const,
          },
        ]
      : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <Profile />;

      case 'general':
        return <General />;

      case 'roles':
        return <Roles renderAction={setRolesAction} />;

      case 'language':
        return (
          <Language settingsData={settingsData} onRefresh={handleGetSettings} />
        );

      case 'account_security':
        return <AccountSecurity />;

      case 'deleted_records':
        return <DeletedRecords />;

      default:
        return <Profile />;
    }
  };

  const handleGetSettings = async () => {
    const response = await getSettings();
    if (response?.status) {
      setSettingsData(response?.data);
    }
  };

  return (
    <div className="space-y-10">
      {/* Tabs */}
      <Tabs
        options={tabs}
        value={activeTab}
        onChange={setActiveTab}
        action={
          activeTab === 'roles' && rolesAction ? (
            <div>{rolesAction}</div>
          ) : undefined
        }
      />

      {/* Content Container */}
      <div
        className={clsx(
          'max-w-216 mx-auto',
          activeTab === 'deleted_records' && 'max-w-full'
        )}
      >
        {/* Content Header */}
        <h2 className="text-xl font-semibold text-neutral-primary mb-6">
          {activeTab === 'profile' && formatText('settings.tabs.profile')}
          {activeTab === 'general' &&
            formatText('settings.tabs.general-settings')}
          {activeTab === 'roles' &&
            formatText('settings.tabs.roles-permissions')}
          {activeTab === 'language' &&
            formatText('settings.tabs.language-region')}
          {activeTab === 'account_security' &&
            formatText('settings.tabs.account-security')}
        </h2>

        {/* Content Card */}
        <div
          className={clsx(
            'bg-white border border-neutral-light-200 rounded-2xl p-6',
            (activeTab === 'roles' ||
              activeTab === 'profile' ||
              activeTab === 'deleted_records') &&
              'p-0! border-0!'
          )}
        >
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
